import { Typography, Box } from '@mui/material'
import React, { useMemo } from 'react'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import Container from './Container'
import { RetrofitRecommendationType, RetrofitRecommendation as RetrofitRecommendationModel, InfinityOption, Retrofit } from '../../models'
import RetrofitRecommendation from './RetrofitRecommendation'
import {
  compareTwoRecommendations,
  checkIfRecommendationOutdated,
  checkIfRecommendationInvalid,
  compareInfinityPlusRecommendations,
} from '../../utils'

const useStyles = makeStyles()(theme => ({
  collapseContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    gap: theme.spacing(2),
  },
}))

interface RecommendationOption {
  title: string
  value: RetrofitRecommendationModel | InfinityOption
  defaultOpened: boolean
  isInfinity?: boolean
}

interface RetrofitRecommendationsProps {
  retrofitData: Retrofit
}

/**
 * Component that displays all retrofit recommendations for the current system
 */
const RetrofitRecommendations: React.FC<RetrofitRecommendationsProps> = ({ retrofitData }) => {
  const { classes } = useStyles()
  const { t, selectedAppLanguage } = useCustomTranslation()
  const { configuration, retrofitRecommendations, infinityOption } = retrofitData
  const isOnlyInfinity = retrofitRecommendations.length === 0 && !!infinityOption

  const recommendationByType: Partial<Record<RetrofitRecommendationType, RetrofitRecommendationModel>> = useMemo(
    () => Object.fromEntries(retrofitRecommendations.map(elem => [elem.type, elem])),
    [retrofitRecommendations]
  )

  const recommendationOptions = useMemo<RecommendationOption[]>(() => {
    const recommendedRecommendation = recommendationByType?.recommended
    const minRecommendation = recommendationByType?.min
    const maxRecommendation = recommendationByType?.big

    let options: RecommendationOption[] = []

    if (
      recommendedRecommendation &&
      !checkIfRecommendationInvalid(recommendedRecommendation) &&
      !checkIfRecommendationOutdated(recommendedRecommendation)
    ) {
      options = [
        {
          title: t('retrofit-check.available.suggestions.recommended-suggestion-title'),
          value: recommendedRecommendation,
          defaultOpened: true,
        },
      ]
    }

    if (
      minRecommendation &&
      !compareTwoRecommendations(minRecommendation, recommendedRecommendation) &&
      !checkIfRecommendationInvalid(minRecommendation) &&
      !checkIfRecommendationOutdated(minRecommendation)
    ) {
      options = [
        {
          title: t('retrofit-check.available.suggestions.min-suggestion-title'),
          value: minRecommendation,
          defaultOpened: false,
        },
        ...options,
      ]
    }

    if (
      maxRecommendation &&
      !compareTwoRecommendations(maxRecommendation, recommendedRecommendation) &&
      !checkIfRecommendationInvalid(maxRecommendation) &&
      !checkIfRecommendationOutdated(maxRecommendation)
    ) {
      options = [
        ...options,
        {
          title: t('retrofit-check.available.suggestions.max-suggestion-title'),
          value: maxRecommendation,
          defaultOpened: false,
        },
      ]
    }

    if (
      infinityOption &&
      !checkIfRecommendationInvalid(infinityOption) &&
      !checkIfRecommendationOutdated(infinityOption) &&
      !compareInfinityPlusRecommendations(infinityOption, recommendedRecommendation) &&
      !compareInfinityPlusRecommendations(infinityOption, maxRecommendation) &&
      !compareInfinityPlusRecommendations(infinityOption, minRecommendation)
    ) {
      options = [
        ...options,
        {
          title: t('retrofit-check.available.suggestions.infinity-suggestion-title'),
          value: infinityOption,
          defaultOpened: isOnlyInfinity,
          isInfinity: true,
        },
      ]
    }

    return options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infinityOption, recommendationByType.big, recommendationByType.min, recommendationByType.recommended, selectedAppLanguage])

  if (!configuration) {
    return null
  }

  return (
    <Container>
      <Typography variant="h2">{t('retrofit-check.available.suggestions.title')}</Typography>
      <Box className={classes.collapseContainer}>
        {recommendationOptions.map(({ title, value, defaultOpened, isInfinity }) => (
          <RetrofitRecommendation
            key={title}
            title={title}
            currentSystemValue={configuration}
            recommendationValue={value}
            defaultOpened={defaultOpened}
            isInfinity={isInfinity}
          />
        ))}
      </Box>
    </Container>
  )
}

export default RetrofitRecommendations
